import {Component, OnDestroy, OnInit} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {MenuitemComponent as AppMenuitem} from '../menuitem/menuitem.component';
import {CommonModule} from '@angular/common';
import {Router, RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {UserValidationService} from '../../services/user-validation.service';
import {AgentStateManagerService} from '../../services/agent-state-manager.service';
import {AuthorizationService} from '../../services/authorization.service';
import {DeviceInfoService} from '../../services/device-info.service';
import {LogoutCoordinatorService} from '../../services/logout-coordinator.service';
import {SharedDataService} from '../../services/shared-data.service';
import {SessionMonitorService} from '../../services/session-monitor.service';
import { WebSocketManagerService } from '../../connectivity';


@Component({
  selector: 'app-menu',
  imports: [
    CommonModule,
    AppMenuitem,
    RouterModule,
    TranslateModule,
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  standalone: true
})
export class MenuComponent implements OnInit, OnDestroy {
  userRole: string = '';
  model: MenuItem[] = [];

  // Component lifecycle management
  private destroy$ = new Subject<void>();
  private isLoggingOut = false;

  constructor(
    private userValidationService: UserValidationService,
    private agentStateSvc: AgentStateManagerService,
    private auth: AuthorizationService,
    private deviceInfoService: DeviceInfoService,
    private router: Router,
    private logoutCoordinator: LogoutCoordinatorService,
    private sharedDataService: SharedDataService,
    private websocketManager: WebSocketManagerService,
    private sessionMonitor: SessionMonitorService
  ) { }


  ngOnInit() {
    this.getRole();
    this.model = this.getGroupedMenuItems();
    this.setupLogoutStatusMonitoring();
  }

  private getGroupedMenuItems(): MenuItem[] {
    const allItems: any[] = [
      {
        label: 'menu.dashboard',
        icon: 'pi pi-fw pi-home',
        routerLink: ['/layout'],
        roles: ['agent', 'supervisor', 'admin', 'superadmin'],
        group: 'menu.group.dashboard'
      },
      {
        label: 'menu.chatAgent',
        icon: 'pi pi-fw pi-comment',
        routerLink: ['/agent-layout'],
        roles: ['agent', 'admin', 'supervisor', 'superadmin'],
        group: 'menu.group.operations'
      },
      {
        label: 'menu.supervisor',
        icon: 'pi pi-user-edit',
        routerLink: ['/supervisor'],
        roles: ['admin', 'supervisor', 'superadmin'],
        group: 'menu.group.administration'
      },
      {
        label: 'menu.settings',
        icon: 'pi pi-fw pi-info-circle',
        routerLink: ['/settings'],
        roles: ['agent', 'supervisor', 'admin', 'superadmin'],
        group: 'menu.group.administration'
      }
    ];

    const filteredItems = allItems.filter(item => item.roles.includes(this.userRole)); // comment to enable role filtering
    // const filteredItems = allItems; // comment to disable role filtering

    const grouped = filteredItems.reduce((acc, item) => {
      const {group, ...restOfItem} = item;
      (acc[group] = acc[group] || []).push(restOfItem);
      return acc;
    }, {} as { [key: string]: MenuItem[] });

    const finalMenu: MenuItem[] = [];
    for (const groupName in grouped) {
      const itemsInGroup = grouped[groupName];

      if (itemsInGroup.length === 1) {
        finalMenu.push(itemsInGroup[0]);
      } else {
        finalMenu.push({
          label: groupName,
          items: itemsInGroup
        });
      }
    }

    return finalMenu;
  }

  getRole() {
    const user = this.sharedDataService.getAgent();
    this.userRole = user?.role ?? '';
  }

  /**
   * Enhanced logout method that coordinates with AppComponent for safe WebSocket disconnection
   */
  async logout(): Promise<void> {
    // Prevent multiple simultaneous logout attempts
    if (this.isLoggingOut || this.logoutCoordinator.isLoggingOut()) {
      // console.log('⚠️ Logout ya en progreso desde MenuComponent');
      return;
    }

    // IMPORTANTE: Marcar logout manual para prevenir popup de sesión cerrada
    console.log('🚪 MenuComponent: Marcando logout manual');
    this.sessionMonitor.markManualLogout();

    this.isLoggingOut = true;
    // console.log('🔐 Iniciando logout desde MenuComponent');

    try {
      const user: any = JSON.parse(localStorage.getItem('logged_in_agent') ?? '{}');
      const token = localStorage.getItem('authToken') || '';

      // Phase 1: Cleanup device info
      // console.log('🧹 Limpiando información del dispositivo');
      this.logoutCoordinator.updateLogoutPhase('device-cleanup');
      // this.deviceInfoService.cleanupDeviceInfo();

      // Phase 2: Coordinate WebSocket disconnection through direct service call
      console.log('🔌 Coordinando desconexión segura de WebSocket');
      this.logoutCoordinator.updateLogoutPhase('websocket-disconnect');
      await this.coordinateWebSocketDisconnection();

      // Phase 3: Perform server logout API
      // console.log('🔐 Realizando logout del servidor');
      this.logoutCoordinator.updateLogoutPhase('server-logout');
      await this.performServerLogout(token);

      // Phase 4: Clean up local storage and session CACHE
      // console.log('🧹 Limpiando almacenamiento local');
      this.logoutCoordinator.updateLogoutPhase('storage-cleanup');
      this.cleanupLocalStorage();

      // Phase 5: Update authorization state
      // console.log('🔑 Actualizando estado de autorización');
      this.auth.sessionSubj.next(null);

      // Phase 6: Navigate to login
      // console.log('🔄 Redirigiendo al login');
      this.router.navigate(['/login']);

      // Phase 7: Complete logout process
      this.logoutCoordinator.completeLogout();

      // console.log('✅ Logout completado exitosamente desde MenuComponent');

    } catch (error) {
      console.error('❌ Error durante logout desde MenuComponent:', error);

      // Notify coordinator about error
      this.logoutCoordinator.handleLogoutError(error);

      // Fallback cleanup - ensure we clean up even if something fails
      this.cleanupLocalStorage();
      this.auth.sessionSubj.next(null);
      this.router.navigate(['/login']);

    } finally {
      this.isLoggingOut = false;
    }
  }

  /**
   * Close agent state with promise wrapper
   */
  private async closeAgentState(agentId: string): Promise<void> {
    if (!agentId) {
      console.warn('⚠️ ID de agente no encontrado, saltando cierre de estado');
      return;
    }

    return new Promise<void>((resolve, reject) => {
      const closeSubscription = this.agentStateSvc
        .closeAgentState(agentId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          complete: () => {
            // console.log('✅ Estado del agente cerrado correctamente');
            resolve();
          },
          error: (error) => {
            console.error('❌ Error cerrando estado del agente:', error);
            reject(error);
          }
        });

      // Cleanup subscription after completion
      closeSubscription.add(() => {
        closeSubscription.unsubscribe();
      });
    });
  }

  /**
   * Coordinate WebSocket disconnection directly to avoid conflicts with AppComponent
   * This method ensures a clean WebSocket disconnection before the logout process continues
   */
  private async coordinateWebSocketDisconnection(): Promise<void> {
    try {
      console.log('🔌 MenuComponent: Iniciando desconexión directa de WebSocket');

      // Check if WebSocket is currently connected
      const isConnected = this.websocketManager.isConnected();
      const connectionInfo = this.websocketManager.getConnectionInfo();

      console.log('📊 Estado actual de WebSocket:', {
        isConnected,
        status: connectionInfo.status,
        networkQuality: connectionInfo.networkQuality,
        circuitState: connectionInfo.circuitState
      });

      if (isConnected) {
        console.log('🔌 Desconectando WebSocket desde MenuComponent...');

        // Direct disconnection using WebSocketManagerService
        this.websocketManager.disconnect();

        // Wait a short moment to ensure disconnection is processed
        await new Promise(resolve => setTimeout(resolve, 500));

        console.log('✅ WebSocket desconectado exitosamente desde MenuComponent');
      } else {
        console.log('ℹ️ WebSocket ya estaba desconectado - no se requiere acción');
      }

      // Update the logout coordinator to indicate WebSocket disconnection phase is complete
      this.logoutCoordinator.updateLogoutPhase('websocket-disconnect');

    } catch (error) {
      console.error('❌ Error en desconexión directa de WebSocket:', error);

      // Force disconnection as fallback
      try {
        this.websocketManager.disconnect();
        console.log('🔧 Desconexión forzada aplicada como respaldo');
      } catch (fallbackError) {
        console.error('❌ Error en desconexión forzada:', fallbackError);
      }

      // Update coordinator about the error but don't throw - continue with logout
      this.logoutCoordinator.handleLogoutError(error);
    }
  }

  /**
   * Perform server logout with promise wrapper
   */
  private async performServerLogout(token: string): Promise<void> {
    try {
      await this.userValidationService.logoutCurrentSession().toPromise();
    } catch (error) {
      console.warn('No fue posible revocar la sesion del auth-service:', error);
    }

    if (!token) {
      console.warn('⚠️ Token no encontrado, saltando logout del servidor');
      return;
    }

    return new Promise<void>((resolve, reject) => {
      const logoutSubscription = this.userValidationService
        .logout(token)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            // console.log('📡 Respuesta de logout recibida');
          },
          complete: () => {
            // console.log('✅ Logout del servidor completado');
            resolve();
          },
          error: (error) => {
            console.error('❌ Error en logout del servidor:', error);
            // Don't reject - we want to continue cleanup even if server logout fails
            resolve();
          }
        });

      // Cleanup subscription after completion
      logoutSubscription.add(() => {
        logoutSubscription.unsubscribe();
      });
    });
  }

  /**
   * Clean up local storage and session storage
   * Note: clearUser() will trigger AppComponent's logout handler via SharedDataService.onUserLogout$
   * Since we've already disconnected WebSocket in coordinateWebSocketDisconnection(),
   * AppComponent's disconnectWebSocket() call will be safe (idempotent)
   */
  private cleanupLocalStorage(): void {
    try {
      console.log('🧹 MenuComponent - Iniciando limpieza de almacenamiento');

      // Use SharedDataService to clear user data (this will emit logout event to AppComponent)
      // Note: This will trigger AppComponent's handleUserLogout() which calls disconnectWebSocket()
      // Since we already disconnected WebSocket above, the AppComponent call will be idempotent
      this.sharedDataService.clearUser();

      // Remove additional specific items
      localStorage.removeItem('authToken');
      localStorage.removeItem('auth_req');
      localStorage.removeItem('status_agent');

      // Clear all storage as fallback
      localStorage.clear();
      sessionStorage.clear();

      console.log('✅ Almacenamiento local limpiado - AppComponent también será notificado');
    } catch (error) {
      console.error('❌ Error limpiando almacenamiento local:', error);
    }
  }

  /**
   * Get logout status for UI feedback
   */
  public isLogoutInProgress(): boolean {
    return this.isLoggingOut || this.logoutCoordinator.isLoggingOut();
  }

  /**
   * Public method to trigger logout (can be called from template)
   */
  public async triggerLogout(): Promise<void> {
    await this.logout();
  }

  /**
   * Get detailed logout status
   */
  public getLogoutStatus(): any {
    return {
      menuComponentLoggingOut: this.isLoggingOut,
      coordinatorStatus: this.logoutCoordinator.getCurrentStatus(),
    };
  }

  /**
   * Emergency logout - for UI button or error scenarios
   */
  public async emergencyLogout(): Promise<void> {
    console.warn('🚨 MenuComponent - Emergency logout triggered');

    try {
      await this.logoutCoordinator.emergencyLogout();
    } catch (error) {
      console.error('❌ Error en emergency logout:', error);
      // Force cleanup anyway
      this.cleanupLocalStorage();
      this.auth.sessionSubj.next(null);
      this.router.navigate(['/login']);
    }
  }

  /**
   * Setup logout status monitoring
   */
  private setupLogoutStatusMonitoring(): void {
    // Monitor logout status changes
    this.logoutCoordinator.onLogoutStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        // console.log(`📊 MenuComponent - Logout status: ${status.phase}`);

        // Update UI based on logout status if needed
        if (status.phase === 'websocket-disconnect') {
          // console.log('🔌 MenuComponent - WebSocket disconnection phase');
        } else if (status.phase === 'redirect') {
          // console.log('🔄 MenuComponent - Redirect phase');
        }
      });

    // Monitor logout events
    this.logoutCoordinator.onLogoutEvent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        // console.log(`📡 MenuComponent - Logout event from ${event.initiator}`);
      });
  }

  ngOnDestroy(): void {
    // console.log('🧹 Destruyendo MenuComponent');
    this.destroy$.next();
    this.destroy$.complete();
  }
}
